import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  useTheme,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import UserForm from './UserForm';
import { makeApiCall } from '../../config/api';
import { formatWageDisplay } from '../../utils/wageUtils';

const UserManagement = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State for users and filters
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Filters
  // searchInput is what's typed; searchTerm (sent to the server) follows it
  // after a short pause so every keystroke doesn't fire a request.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userType, setUserType] = useState('');
  const [isActive, setIsActive] = useState('true');
  // Only the newest request may update the list; a slower reply for an
  // earlier search must not overwrite it (TKT-564).
  const latestFetch = useRef(0);
  
  // Dialog states
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openEditUser, setOpenEditUser] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openLinkEmployee, setOpenLinkEmployee] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Employee linking
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [linkingEmployee, setLinkingEmployee] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({});
  
  // Lookup data
  const [lookups, setLookups] = useState({
    departments: [],
    job_titles: [],
    employee_positions: []
  });

  // Fetch users
  const fetchUsers = async () => {
    const requestId = ++latestFetch.current;
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        user_type: userType,
        is_active: isActive,
        sort_by: 'created_at',
        sort_order: 'DESC'
      });

      const response = await makeApiCall(`api/users?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (requestId !== latestFetch.current) return;
      setUsers(data.users);
      setTotalUsers(data.pagination.total);

    } catch (error) {
      if (requestId !== latestFetch.current) return;
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      if (requestId === latestFetch.current) setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await makeApiCall('api/users/stats/overview');

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch lookup data
  const fetchLookups = async () => {
    try {
      const response = await makeApiCall('api/users/lookups/all');

      if (response.ok) {
        const data = await response.json();
        setLookups(data);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    try {
      const response = await makeApiCall(`api/users/${selectedUser.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('User deleted successfully');
        fetchUsers();
        fetchStats();
        setOpenDeleteConfirm(false);
        setSelectedUser(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  // Handle form success
  const handleFormSuccess = (message) => {
    setSuccess(message);
    fetchUsers();
    fetchStats();
    setOpenAddUser(false);
    setOpenEditUser(false);
    setSelectedUser(null);
  };

  // Fetch available employees for linking
  const fetchAvailableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await makeApiCall('api/employees?is_active=true&has_user=false&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setAvailableEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load available employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle opening link employee dialog
  const handleOpenLinkEmployee = (userData) => {
    setSelectedUser(userData);
    setSelectedEmployee(null);
    setOpenLinkEmployee(true);
    fetchAvailableEmployees();
  };

  // Handle linking user to employee
  const handleLinkEmployee = async () => {
    if (!selectedEmployee || !selectedUser) {
      setError('Please select an employee');
      return;
    }

    setLinkingEmployee(true);
    try {
      const response = await makeApiCall(`api/users/${selectedUser.id}/link-employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: selectedEmployee.id })
      });

      if (response.ok) {
        setSuccess(`Successfully linked ${selectedUser.username} to ${selectedEmployee.employee_name}`);
        setOpenLinkEmployee(false);
        setSelectedUser(null);
        setSelectedEmployee(null);
        fetchUsers();
        fetchStats();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to link employee');
      }
    } catch (error) {
      console.error('Error linking employee:', error);
      setError('Failed to link employee');
    } finally {
      setLinkingEmployee(false);
    }
  };

  // Get user type color
  const getUserTypeColor = (type) => {
    switch (type) {
      case 'admin': return 'error';
      case 'superadmin': return 'warning';
      case 'normal': return 'primary';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  // Get role badges
  const getRoleBadges = (userData) => {
    const badges = [];
    if (userData.is_sales_person) badges.push({ label: 'Sales', color: 'success' });
    if (userData.is_manager) badges.push({ label: 'Manager', color: 'info' });
    return badges;
  };

  // TKT-564: search after a short pause in typing, and always from page 1.
  // The page used to stay where it was, so searching while on a later page
  // (e.g. after paging through Inactive to find someone) showed that page of
  // the results, which is empty for a search with one match ("Calvin").
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setPage(0);
        setSearchTerm(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  // Use effect to fetch data
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, userType, isActive]);

  useEffect(() => {
    fetchStats();
    fetchLookups();
  }, []);

  // Stats cards data
  const statsCards = [
    { 
      title: 'Total Users', 
      value: stats.total_users || 0, 
      icon: <PeopleIcon />, 
      color: 'primary.main' 
    },
    { 
      title: 'Active Users', 
      value: stats.active_users || 0, 
      icon: <PersonIcon />, 
      color: 'success.main' 
    },
    { 
      title: 'Employees', 
      value: stats.total_employees || 0, 
      icon: <WorkIcon />, 
      color: 'info.main' 
    },
    { 
      title: 'Sales Staff', 
      value: stats.sales_persons || 0, 
      icon: <BadgeIcon />, 
      color: 'warning.main' 
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-users-root">
      {/* Page Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 3
          }}
        >
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddUser(true)}
          sx={{ 
            bgcolor: '#c62828', 
            '&:hover': { bgcolor: '#a61e1e' },
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          Add User
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {statsCards.map((stat, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      color="textSecondary" 
                      gutterBottom 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography 
                      variant="h4" 
                      component="h2"
                      sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: stat.color, 
                    width: { xs: 40, md: 56 }, 
                    height: { xs: 40, md: 56 }
                  }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Users"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                size="small"
                // TKT-538/564: keep the browser from filling in a saved login
                // name here, which would quietly filter the list.
                inputProps={{
                  autoComplete: 'off',
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                  'data-form-type': 'other',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userType}
                  label="User Type"
                  onChange={(e) => { setUserType(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="superadmin">Super Admin</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="subcontractor">Subcontractor</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive}
                  label="Status"
                  onChange={(e) => { setIsActive(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Users</MenuItem>
                  <MenuItem value="true">Active Only</MenuItem>
                  <MenuItem value="false">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton 
                      onClick={fetchUsers} 
                      color="primary" 
                      size="medium"
                      disabled={loading}
                      sx={{ p: { xs: 1, md: 1.5 } }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: { xs: 0, md: 2 } }}>
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Type & Roles</TableCell>
                    <TableCell>Employee Info</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography>Loading users...</Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography>No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((userData) => (
                    <TableRow key={userData.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {userData.first_name?.[0] || userData.username?.[0] || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                              {userData.first_name} {userData.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              @{userData.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">{userData.email}</Typography>
                          </Box>
                          {userData.employee_name && (
                            <Typography variant="body2" color="text.secondary">
                              Employee: {userData.employee_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={userData.user_type}
                            color={getUserTypeColor(userData.user_type)}
                            size="small"
                            sx={{ mb: 1, textTransform: 'capitalize' }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {getRoleBadges(userData).map((badge, index) => (
                              <Chip
                                key={index}
                                label={badge.label}
                                color={badge.color}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {userData.employee_id ? (
                          <Box>
                            {userData.department_name && (
                              <Typography variant="body2">
                                Dept: {userData.department_name}
                              </Typography>
                            )}
                            {userData.job_title && (
                              <Typography variant="body2" color="text.secondary">
                                {userData.job_title}
                              </Typography>
                            )}
                            {userData.wage && (
                              <Typography variant="body2" color="text.secondary">
                                {formatWageDisplay(userData.wage, userData.wage_type)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                              No employee record
                            </Typography>
                            <Tooltip title="Link to Employee">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenLinkEmployee(userData)}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={userData.is_active ? 'Active' : 'Inactive'}
                          color={userData.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(userData.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUser(userData);
                            setOpenEditUser(true);
                          }}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUser(userData);
                            setOpenDeleteConfirm(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {loading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>Loading users...</Typography>
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No users found</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                {users.map((userData) => (
                  <Card 
                    key={userData.id} 
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center" flex={1}>
                          <Avatar sx={{ mr: 1.5, bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                            {userData.first_name?.[0] || userData.username?.[0] || 'U'}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              {userData.first_name} {userData.last_name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                              @{userData.username}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={userData.is_active ? 'Active' : 'Inactive'}
                            color={userData.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(userData);
                                setOpenEditUser(true);
                              }}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(userData);
                                setOpenDeleteConfirm(true);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                        <Box>
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {userData.email?.length > 20 ? `${userData.email.substring(0, 20)}...` : userData.email}
                            </Typography>
                          </Box>
                          <Chip
                            label={userData.user_type}
                            color={getUserTypeColor(userData.user_type)}
                            size="small"
                            sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: '20px' }}
                          />
                        </Box>
                        <Box>
                          {userData.employee_name ? (
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                              Employee: {userData.employee_name}
                            </Typography>
                          ) : (
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                No employee
                              </Typography>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenLinkEmployee(userData)}
                                sx={{ p: 0.25 }}
                              >
                                <LinkIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {getRoleBadges(userData).map((badge, index) => (
                              <Chip
                                key={index}
                                label={badge.label}
                                color={badge.color}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: '18px' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                        Created: {new Date(userData.created_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ mt: { xs: 1, md: 0 } }}
          />
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog 
        open={openAddUser} 
        onClose={() => setOpenAddUser(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Add New User</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          <UserForm
            onSuccess={handleFormSuccess}
            onCancel={() => setOpenAddUser(false)}
            lookups={lookups}
          />
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog 
        open={openEditUser} 
        onClose={() => setOpenEditUser(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Edit User</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          {selectedUser && (
            <UserForm
              user={selectedUser}
              onSuccess={handleFormSuccess}
              onCancel={() => setOpenEditUser(false)}
              lookups={lookups}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.username}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Employee Dialog */}
      <Dialog 
        open={openLinkEmployee} 
        onClose={() => setOpenLinkEmployee(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Link User to Employee
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Link <strong>{selectedUser?.username}</strong> to an employee record to connect their system login with HR data, timesheets, and vehicle assignments.
            </Typography>
            
            <Autocomplete
              fullWidth
              options={availableEmployees}
              getOptionLabel={(option) => option.employee_name || ''}
              value={selectedEmployee}
              onChange={(event, newValue) => setSelectedEmployee(newValue)}
              loading={loadingEmployees}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  placeholder="Search for an employee..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">{option.employee_name}</Typography>
                    {option.department_name && (
                      <Typography variant="caption" color="text.secondary">
                        {option.department_name} {option.job_title && `- ${option.job_title}`}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText={loadingEmployees ? "Loading employees..." : "No available employees (all may already have user accounts)"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenLinkEmployee(false);
              setSelectedEmployee(null);
            }}
            disabled={linkingEmployee}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLinkEmployee}
            variant="contained"
            disabled={!selectedEmployee || linkingEmployee}
            startIcon={linkingEmployee ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {linkingEmployee ? 'Linking...' : 'Link Employee'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement; 